import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Building2, Award, MessageSquare, TrendingUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Aktive Immobilien', 
      value: stats?.properties || 0, 
      icon: Building2,
      href: '/admin/properties'
    },
    { 
      title: 'Referenzen', 
      value: stats?.references || 0, 
      icon: Award,
      href: '/admin/references'
    },
    { 
      title: 'Neue Anfragen', 
      value: stats?.new_leads || 0, 
      icon: MessageSquare,
      href: '/admin/leads',
      highlight: true
    },
    { 
      title: 'Gesamt Anfragen', 
      value: stats?.total_leads || 0, 
      icon: TrendingUp,
      href: '/admin/leads'
    }
  ];

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-display text-3xl text-white mb-2">
          Willkommen, {user?.name || 'Admin'}
        </h1>
        <p className="text-[#c8c6c4]">
          Hier ist Ihre Übersicht
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => (
            <motion.a
              key={card.title}
              href={card.href}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`card-luxury p-6 group ${card.highlight ? 'border-t-[#d1af6a]' : ''}`}
              data-testid={`stat-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 flex items-center justify-center ${card.highlight ? 'bg-[#d1af6a]/20' : 'bg-[#d1af6a]/10'}`}>
                  <card.icon className="w-6 h-6 text-[#d1af6a]" />
                </div>
                {card.highlight && card.value > 0 && (
                  <span className="bg-[#d1af6a] text-[#0a1120] text-xs font-bold px-2 py-1">
                    NEU
                  </span>
                )}
              </div>
              <p className="text-[#c8c6c4] text-sm mb-1">{card.title}</p>
              <p className="font-display text-3xl text-white group-hover:text-[#d1af6a] transition-colors">
                {card.value}
              </p>
            </motion.a>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-12">
        <h2 className="font-subheading text-xl text-white mb-6">Schnellaktionen</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a 
            href="/admin/properties" 
            className="card-luxury p-4 text-center hover:border-t-[#d1af6a] transition-colors"
          >
            <span className="text-[#c8c6c4]">+ Neue Immobilie</span>
          </a>
          <a 
            href="/admin/references" 
            className="card-luxury p-4 text-center hover:border-t-[#d1af6a] transition-colors"
          >
            <span className="text-[#c8c6c4]">+ Neue Referenz</span>
          </a>
          <a 
            href="/admin/leads" 
            className="card-luxury p-4 text-center hover:border-t-[#d1af6a] transition-colors"
          >
            <span className="text-[#c8c6c4]">Anfragen ansehen</span>
          </a>
        </div>
      </div>
    </div>
  );
}
